import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import {
  GoodsReceiptRecord,
  QualityInspectionItemInput,
  RejectionReasonCode,
  QualityInspectionStatus,
} from '@/types/domain/grn-management';
import { ShieldCheck, AlertTriangle, X, RefreshCw, CheckCircle2 } from 'lucide-react';
import { calculateAcceptedQuantity } from '@/features/grn/utils/grnCalculations';

interface GrnInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  grn: GoodsReceiptRecord;
  onConfirm: (payload: {
    itemInspections: QualityInspectionItemInput[];
    inspectorNotes?: string;
    overallQualityStatus: QualityInspectionStatus;
  }) => Promise<void>;
}

export const GrnInspectionModal: React.FC<GrnInspectionModalProps> = ({
  isOpen,
  onClose,
  grn,
  onConfirm,
}) => {
  const [inspections, setInspections] = useState<
    {
      grnItemId: string;
      materialCode: string;
      description: string;
      receivedQuantity: number;
      rejectedQuantity: number;
      acceptedQuantity: number;
      rejectionReason?: RejectionReasonCode;
      rejectionNotes?: string;
    }[]
  >([]);

  const [inspectorNotes, setInspectorNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (grn) {
      setInspections(
        grn.items.map((it) => ({
          grnItemId: it.id,
          materialCode: it.materialCode,
          description: it.description,
          receivedQuantity: it.receivedQuantity,
          rejectedQuantity: it.rejectedQuantity || 0,
          acceptedQuantity: it.acceptedQuantity,
          rejectionReason: it.rejectionReason,
          rejectionNotes: it.rejectionNotes || '',
        }))
      );
    }
  }, [grn]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleRejectedQtyChange = (index: number, val: number) => {
    const safeVal = Math.max(0, val);
    setInspections((prev) => {
      const updated = [...prev];
      const target = { ...updated[index]! };
      if (safeVal > target.receivedQuantity) {
        setError(`Rejected quantity cannot exceed received quantity (${target.receivedQuantity}).`);
      } else {
        setError(null);
      }
      target.rejectedQuantity = safeVal;
      target.acceptedQuantity = calculateAcceptedQuantity(target.receivedQuantity, safeVal);
      updated[index] = target;
      return updated;
    });
  };

  const handleReasonChange = (index: number, reason: RejectionReasonCode) => {
    setInspections((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, rejectionReason: reason };
      return updated;
    });
  };

  const handleItemNotesChange = (index: number, notes: string) => {
    setInspections((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, rejectionNotes: notes };
      return updated;
    });
  };

  const handleSaveInspection = async () => {
    // Validate each line
    for (const item of inspections) {
      if (item.rejectedQuantity > item.receivedQuantity) {
        setError(`Item ${item.materialCode}: rejected quantity (${item.rejectedQuantity}) exceeds received quantity (${item.receivedQuantity}).`);
        return;
      }
      if (item.rejectedQuantity > 0 && !item.rejectionReason) {
        setError(`Item ${item.materialCode} has ${item.rejectedQuantity} rejected units but no rejection reason specified.`);
        return;
      }
    }

    const totalAccepted = inspections.reduce((s, it) => s + it.acceptedQuantity, 0);
    const totalRejected = inspections.reduce((s, it) => s + it.rejectedQuantity, 0);

    let overallStatus: QualityInspectionStatus = 'PASSED';
    if (totalRejected > 0 && totalAccepted === 0) {
      overallStatus = 'FAILED';
    } else if (totalRejected > 0) {
      overallStatus = 'PARTIALLY_PASSED';
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onConfirm({
        itemInspections: inspections.map((it) => ({
          grnItemId: it.grnItemId,
          acceptedQuantity: it.acceptedQuantity,
          rejectedQuantity: it.rejectedQuantity,
          rejectionReason: it.rejectionReason,
          rejectionNotes: it.rejectionNotes,
        })),
        inspectorNotes,
        overallQualityStatus: overallStatus,
      });
      onClose();
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to record inspection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-foreground">
                Record Warehouse Quality Inspection — {grn.grnNumber}
              </h3>
              <p className="text-xs text-foreground-secondary">
                PO: <span className="font-mono font-bold text-foreground">{grn.poNumber}</span> | Supplier: {grn.vendorName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-800 text-xs font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        {/* Line Items Inspection Table */}
        <div className="space-y-4">
          <span className="text-xs font-black uppercase tracking-wider text-foreground">
            Line Items Inspection & Quantity Reconciliation
          </span>

          <div className="space-y-3">
            {inspections.map((item, idx) => (
              <div
                key={item.grnItemId || idx}
                className="p-4 rounded-xl border border-border/80 bg-surface/50 space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2">
                  <div>
                    <span className="text-xs font-black text-foreground">{item.description}</span>
                    <span className="text-[10px] text-foreground-muted font-mono block">Code: {item.materialCode}</span>
                  </div>
                  <div className="text-xs font-bold text-foreground-secondary">
                    Total Delivered: <span className="font-mono text-primary font-black">{item.receivedQuantity}</span> units
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block text-[10px] font-extrabold uppercase text-foreground-secondary mb-1">
                      Rejected Quantity
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={item.receivedQuantity}
                      value={item.rejectedQuantity}
                      onChange={(e) => handleRejectedQtyChange(idx, Number(e.target.value))}
                      className="w-full h-9 px-3 rounded-xl border border-border bg-card text-foreground font-mono font-bold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-extrabold uppercase text-emerald-600 mb-1">
                      Accepted Net Quantity
                    </label>
                    <div className="h-9 px-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-emerald-700 font-mono font-black text-xs flex items-center">
                      {item.acceptedQuantity} units
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-extrabold uppercase text-foreground-secondary mb-1">
                      Rejection Reason
                    </label>
                    <select
                      disabled={item.rejectedQuantity === 0}
                      value={item.rejectionReason || ''}
                      onChange={(e) => handleReasonChange(idx, e.target.value as RejectionReasonCode)}
                      className="w-full h-9 px-3 rounded-xl border border-border bg-card text-foreground text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                    >
                      <option value="">Select Reason</option>
                      <option value="QUALITY_FAILURE">Quality Failure (Tolerance)</option>
                      <option value="DAMAGED">Physical Damage in Transit</option>
                      <option value="WRONG_MATERIAL">Wrong Material / SKU</option>
                      <option value="WRONG_QUANTITY">Quantity Discrepancy</option>
                      <option value="SPECIFICATION_MISMATCH">Spec Mismatch</option>
                      <option value="PACKAGING_ISSUE">Packaging Defect</option>
                      <option value="OTHER">Other Defect</option>
                    </select>
                  </div>

                  {item.rejectedQuantity > 0 && (
                    <div className="sm:col-span-3">
                      <label className="block text-[10px] font-bold text-foreground-secondary mb-1">
                        Defect Notes / Inspection Observations
                      </label>
                      <input
                        type="text"
                        placeholder="Details of visual or dimensional defect..."
                        value={item.rejectionNotes}
                        onChange={(e) => handleItemNotesChange(idx, e.target.value)}
                        className="w-full h-8 px-3 rounded-xl border border-border bg-card text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-foreground">
              Overall Quality Inspector Remarks / Certification
            </label>
            <textarea
              rows={2}
              value={inspectorNotes}
              onChange={(e) => setInspectorNotes(e.target.value)}
              placeholder="e.g. Completed random sampling per MIL-STD-105E. Batch passed electrical & dimensional inspection."
              className="w-full text-xs font-medium p-3 rounded-xl border border-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="rounded-xl font-bold">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSaveInspection}
            disabled={isSubmitting}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Saving...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Save Quality Inspection
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
